import { api, Area, areaParams } from '../../api';
import { formatClock, formatPartyDate, ModerationAction } from '../poolParties/api';

// Venue events (c2n.api EventEndpoints.cs). Dates are "yyyy-MM-dd", times "HH:mm:ss" (venue time).

export const EVENT_CATEGORIES = ['DJ Night', 'Live Music', 'Ladies Night', 'Theme Party', 'Festival', 'Comedy', 'Sports Screening', 'Other'] as const;
export const MAX_EVENT_DESCRIPTION = 1000;

export interface PublicEvent {
  id: number;
  restoId: number;
  venueName: string;
  address: string;
  state?: string | null;
  district?: string | null;
  latitude: number;
  longitude: number;
  distanceKm?: number | null;
  title: string;
  category: string;
  description: string;
  eventDate: string;
  startTime: string;
  endTime?: string | null;
  entryInfo?: string | null;
  posterUrl?: string | null;
  status: 'Upcoming' | 'Today' | 'Past';
}

export interface EventInput {
  title: string;
  category: string;
  description: string;
  eventDate: string;
  startTime: string;
  endTime?: string | null;
  entryInfo?: string | null;
  isPublished: boolean;
}

export type PartnerEventStatus = 'Draft' | 'Upcoming' | 'Today' | 'Past' | 'Suspended' | 'Rejected' | 'Pending';

export interface PartnerEvent extends EventInput {
  id: number;
  endTime?: string | null;
  status: PartnerEventStatus;
  moderationStatus: string;
  moderationNote?: string | null;
  posterUrl?: string | null;
  updatedAt: string;
}

export interface AdminEventRow {
  id: number;
  restoId: number;
  venueName: string;
  title: string;
  category: string;
  eventDate: string;
  startTime: string;
  status: PartnerEventStatus;
  moderationStatus: 'Pending' | 'Approved' | 'Rejected' | 'Suspended';
  moderationNote?: string | null;
  description: string;
  updatedAt: string;
}

// ---------- Public ----------

export const getEvents = async (lat: number, lng: number, area?: Area): Promise<PublicEvent[]> =>
  (await api.get<PublicEvent[]>('/api/events', { params: { lat, lng, ...areaParams(area) } })).data;

export const getVenueEvents = async (restoId: number): Promise<PublicEvent[]> =>
  (await api.get<PublicEvent[]>('/api/events', { params: { restoId } })).data;

export const getEvent = async (id: number): Promise<PublicEvent> => (await api.get<PublicEvent>(`/api/events/${id}`)).data;

// ---------- Partner portal (the admin console's ?restoId= is added by the shared api client) ----------

export const getMyEvents = async (): Promise<PartnerEvent[]> => (await api.get<PartnerEvent[]>('/api/account/events')).data;

export const createEvent = async (input: EventInput): Promise<PartnerEvent> => (await api.post<PartnerEvent>('/api/account/events', input)).data;

export const saveEvent = async (id: number, input: EventInput): Promise<PartnerEvent> =>
  (await api.put<PartnerEvent>(`/api/account/events/${id}`, input)).data;

export const deleteEvent = async (id: number): Promise<void> => {
  await api.delete(`/api/account/events/${id}`);
};

export async function uploadEventPoster(id: number, file: File): Promise<PartnerEvent> {
  const form = new FormData();
  form.append('file', file);
  return (await api.post<PartnerEvent>(`/api/account/events/${id}/poster`, form)).data;
}

export const removeEventPoster = async (id: number): Promise<PartnerEvent> =>
  (await api.delete<PartnerEvent>(`/api/account/events/${id}/poster`)).data;

// ---------- Admin ----------

export const getAdminEvents = async (): Promise<AdminEventRow[]> => (await api.get<AdminEventRow[]>('/api/admin/events')).data;

export const moderateEvent = async (id: number, action: ModerationAction, note?: string): Promise<{ message: string }> =>
  (await api.post<{ message: string }>(`/api/admin/events/${id}/moderate`, { action, note })).data;

// ---------- Formatting ----------

// "Fri, 3 Oct · 9:00 PM – 1:00 AM" (or "· from 9:00 PM" without an end time).
export const eventWhen = (e: { eventDate: string; startTime: string; endTime?: string | null }) =>
  `${formatPartyDate(e.eventDate)} · ${e.endTime ? `${formatClock(e.startTime)} – ${formatClock(e.endTime)}` : `from ${formatClock(e.startTime)}`}`;

export const eventPlace = (e: Pick<PublicEvent, 'district' | 'state' | 'address'>) =>
  [e.district, e.state].filter(Boolean).join(', ') || e.address;
